import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";

const MySwal = withReactContent(Swal);

// Modal Component for Add/Edit Partner
const PartnerModal = ({ isOpen, onClose, onSubmit, initialData, isEdit }) => {
    const [formData, setFormData] = useState(initialData || { image: null });
    const [errors, setErrors] = useState({});
    const [imagePreview, setImagePreview] = useState(
        isEdit && initialData?.image
            ? `https://ligasos-laravel.ligasos.co/storage/app/public/${initialData.image}`
            : null
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { files } = e.target;
        if (files[0]) {
            setFormData({ ...formData, image: files[0] });
            setImagePreview(URL.createObjectURL(files[0]));
            setErrors({ ...errors, image: "" });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.image) newErrors.image = "Image is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            setIsSubmitting(true);
            const data = new FormData();
            data.append("image", formData.image);
            await onSubmit(data);
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[300]">
            <div className="bg-[#0B2C3D] p-8 rounded-2xl shadow-2xl w-full max-w-lg transform transition-all duration-300">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-extrabold text-white">{isEdit ? "Edit Partner" : "Add Partner"}</h3>
                    <button onClick={onClose} className="text-white hover:text-[#B39359] transition-colors duration-200">
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>
                <form onSubmit={handleFormSubmit} className="space-y-5">
                    <div>
                        <label className="block text-sm font-medium text-white">Image</label>
                        <input
                            type="file"
                            name="image"
                            accept="image/*"
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-[#B39359] file:text-white hover:file:bg-[#D4A373] transition-colors duration-300 disabled:opacity-50"
                        />
                        {errors.image && <p className="text-red-400 text-sm mt-1">{errors.image}</p>}
                        {imagePreview && (
                            <img
                                src={imagePreview}
                                alt="Preview"
                                className="mt-3 w-20 h-20 rounded-full object-cover border-2 border-[#B39359]"
                            />
                        )}
                    </div>
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSubmitting}
                            className="py-2 px-5 rounded-lg bg-gray-600 text-white hover:bg-gray-700 transition-colors duration-300 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="py-2 px-5 rounded-lg bg-[#B39359] text-white hover:bg-[#D4A373] transition-colors duration-300 disabled:opacity-50 flex items-center gap-2"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                                    Processing...
                                </>
                            ) : (
                                <>{isEdit ? "Update" : "Add"}</>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default function Partners() {
    const [partners, setPartners] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editPartner, setEditPartner] = useState(null);
    const token = localStorage.getItem("token");

    // Fetch all partners
    useEffect(() => {
        const fetchPartners = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get("https://ligasos-laravel.ligasos.co/api/partners", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setPartners(response.data);
            } catch (err) {
                MySwal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Failed to fetch partners. Please try again.",
                    confirmButtonColor: "#B39359",
                });
            } finally {
                setIsLoading(false);
            }
        };
        fetchPartners();
    }, [token]);

    // Handle Add Partner
    const handleAddPartner = async (data) => {
        setIsLoading(true);
        try {
            const response = await axios.post(
                "https://ligasos-laravel.ligasos.co/api/partners",
                data,
                { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
            );
            setPartners([...partners, response.data.data]);
            setIsAddModalOpen(false);
            MySwal.fire({
                icon: "success",
                title: "Success",
                text: "Partner added successfully!",
                confirmButtonColor: "#B39359",
                timer: 1500,
                showConfirmButton: false,
            });
        } catch (err) {
            MySwal.fire({
                icon: "error",
                title: "Error",
                text: err.response?.data?.message || "Failed to add partner.",
                confirmButtonColor: "#B39359",
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle Edit Partner
    const handleEditPartner = async (data) => {
        setIsLoading(true);
        try {
            const response = await axios.post(
                `https://ligasos-laravel.ligasos.co/api/partners/${editPartner.id}?_method=PUT`,
                data,
                { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
            );
            setPartners(partners.map((p) => (p.id === editPartner.id ? response.data.data : p)));
            setIsEditModalOpen(false);
            setEditPartner(null);
            MySwal.fire({
                icon: "success",
                title: "Success",
                text: "Partner updated successfully!",
                confirmButtonColor: "#B39359",
                timer: 1500,
                showConfirmButton: false,
            });
        } catch (err) {
            MySwal.fire({
                icon: "error",
                title: "Error",
                text: err.response?.data?.message || "Failed to update partner.",
                confirmButtonColor: "#B39359",
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle Delete Partner
    const handleDeletePartner = async (id) => {
        const result = await MySwal.fire({
            title: "Are you sure?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Delete",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#B39359",
            cancelButtonColor: "#dc2626",
        });

        if (result.isConfirmed) {
            setIsLoading(true);
            try {
                await axios.delete(`https://ligasos-laravel.ligasos.co/api/partners/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setPartners(partners.filter((p) => p.id !== id));
                MySwal.fire({
                    icon: "success",
                    title: "Deleted",
                    text: "Partner deleted successfully!",
                    confirmButtonColor: "#B39359",
                    timer: 1500,
                    showConfirmButton: false,
                });
            } catch (err) {
                MySwal.fire({
                    icon: "error",
                    title: "Error",
                    text: err.response?.data?.message || "Failed to delete partner.",
                    confirmButtonColor: "#B39359",
                });
            } finally {
            setIsLoading(false);
            }
        }
    };

    return (
        <section className="p-8 min-h-screen">
            <div className="">
                <div className="flex justify-between items-center mb-8">
                    <h2 className="text-4xl font-extrabold text-[#0B2C3D] tracking-tight">Partners</h2>
                    <button
                        onClick={() => setIsAddModalOpen(true)}
                        disabled={isLoading}
                        className="flex items-center gap-2 bg-[#B39359] text-white font-semibold py-3 px-6 rounded-lg hover:bg-[#D4A373] transition-colors duration-300 disabled:opacity-50 shadow-md"
                    >
                        <PlusIcon className="w-5 h-5" />
                        Add Partner
                    </button>
                </div>

                {isLoading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#B39359]"></div>
                    </div>
                ) : partners.length === 0 ? (
                    <p className="text-center text-gray-500 text-lg font-medium">No partners found.</p>
                ) : (
                    <div className="overflow-x-auto bg-white rounded-xl shadow-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-[#0B2C3D] sticky top-0 z-10">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Image</th>
                                    <th className="px-6 py-4 text-right text-sm font-semibold text-white uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {partners.map((partner, index) => (
                                    <tr
                                        key={partner.id}
                                        className={`hover:bg-gray-50 transition-colors duration-200 ${index % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                                    >
                                        <td className="px-6 py-4">
                                            {partner.image_path ? (
                                                <img
                                                    src={`https://ligasos-laravel.ligasos.co/storage/app/public/${partner.image_path}`}
                                                    alt="Partner"
                                                    className="w-12 h-12 object-cover border-2 border-[#B39359]"
                                                    onError={(e) => (e.target.src = "https://via.placeholder.com/40")}
                                                />
                                            ) : (
                                                <div className="w-12 h-12 bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-medium">
                                                    No Image
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={() => {
                                                        setEditPartner(partner);
                                                        setIsEditModalOpen(true);
                                                    }}
                                                    className="p-2 text-[#0B2C3D] hover:bg-[#0B2C3D] hover:text-white rounded-full transition-colors duration-300"
                                                    disabled={isLoading}
                                                >
                                                    <PencilIcon className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeletePartner(partner.id)}
                                                    className="p-2 text-red-600 hover:bg-red-600 hover:text-white rounded-full transition-colors duration-300"
                                                    disabled={isLoading}
                                                >
                                                    <TrashIcon className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modals */}
            <PartnerModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onSubmit={handleAddPartner}
                isEdit={false}
            />
            <PartnerModal
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setEditPartner(null);
                }}
                onSubmit={handleEditPartner}
                initialData={editPartner}
                isEdit={true}
            />
        </section>
    );
}